import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { SchemaField, SystemFieldType, DocumentSchema, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { FilterGroup, Filter, RuleOperator, DateFilterOptions } from 'src/app/modules/core/store/models/filters.model';
import { Workflow, WorkflowStep } from 'src/app/modules/core/store/models/workflow.model';
import { GlobalEventListenerService } from '../../../services/global-event-listener.service';
import { GeofenceService } from 'src/app/modules/core/services/geofence.service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { PicklistService } from '../../../services/picklist/picklist.service';
import { LinkFilter, LinkFilterGroup } from '../../../store/models/link-filters.model';

@Component({
    selector: 'topo-link-filter-dataset-item',
    templateUrl: './filter-dataset-item.component.html',
    styleUrls: ['./filter-dataset-item.component.scss'],
    standalone: false
})
export class LinkFilterDatasetItemComponent implements OnInit, OnDestroy {
  @Input() filterGroup: LinkFilterGroup;
  @Input() set dataset(value: Dataset) {
    this._dataset = value;
    if (!!this._filter && !!this.dataset) {
      this.loadData();
    }
  }
  get dataset(): Dataset {
    return this._dataset;
  }
  private _dataset: Dataset;
  @Input() targetDataset: Dataset;
  @Input() unitsConversionValue: number;
  @Input() lookupTargetSchemaFields: SchemaField[];
  @Input() set filter(value: LinkFilter) {
    this._filter = value;
    if (!!this._filter && !!this.dataset) {
      this.loadData();
    }
  }
  get filter(): LinkFilter {
    return this._filter;
  }
  @Output() filterChanged: EventEmitter<LinkFilter> = new EventEmitter<LinkFilter>();

  private _filter: LinkFilter;
  public SchemaDataType = FieldDataType;
  public RuleOperator = RuleOperator;
  public DateFilterOptions = DateFilterOptions;
  schemaFields: SchemaField[];
  toSchemaFields: SchemaField[];
  operators: { name: string; value: RuleOperator }[];
  filterDataType: FieldDataType;
  operatorDisplayValues: any;
  picklistValues = {};
  isGenericFilter: boolean;
  isWorkflowFilter: boolean;
  isDateTimeFilter: boolean;
  isBooleanFilter: boolean;
  isDistanceFilter: boolean;
  isPicklistFilter: boolean;
  isInFilter: boolean;
  isGeofenceFilter: boolean;
  lastFilterValue: any;
  showSelection: boolean;
  showSelectionModal: boolean;
  destroy$ = new Subject<void>();
  clickSubscription: Subscription = null;
  inFilterText: string;
  geofenceDatasets: Dataset[];
  geofenceDatasetUId: string;
  geofenceDocuments: Document[];
  geofenceDocumentUId: string;
  datasetChanged: boolean;

  constructor(
    private translate: TranslateService,
    private eventListenerService: GlobalEventListenerService,
    private geofenceService: GeofenceService,
    private picklistService: PicklistService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  private loadData(): void {
    this.populateOperatorDisplayValues();
    this.schemaFields = this.getSchemaFields(this.dataset?.schema?.fields);
    this.operators = this.getOperators(this.filter);
    this.filterDataType = this.getSelectedDataType(this.filter);
    this.getPicklistValues(this.dataset?.schema);
    this.setFilterTypeFlags();
    this.getToFields();
    if (!!this.filter) {
      this.getGeofenceValues(this.filter);
    }
    this.geofenceService.geofenceDatasets$.pipe(takeUntil(this.destroy$)).subscribe(ds => {
      this.geofenceDatasets = ds;
      this.changeDetector.markForCheck();
    });

    if (!this.geofenceService.isInitialized) {
      this.geofenceService.getGeofenceDatasets();
    }
  }

  populateOperatorDisplayValues() {
    this.operatorDisplayValues = {
      [RuleOperator.Equal]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Equal}`),
      [RuleOperator.NotEqual]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotEqual}`),
      [RuleOperator.GreaterThan]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.GreaterThan}`),
      [RuleOperator.GreaterThanEqualTo]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.GreaterThanEqualTo}`),
      [RuleOperator.LessThan]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.LessThan}`),
      [RuleOperator.LessThanEqualTo]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.LessThanEqualTo}`),
      [RuleOperator.Contains]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Contains}`),
      [RuleOperator.Proximity]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Proximity}`),
      [RuleOperator.DateIsWithinLast]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsWithinLast}`),
      [RuleOperator.DoesNotContain]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DoesNotContain}`),
      [RuleOperator.DateIsNotWithinLast]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsNotWithinLast}`),
      [RuleOperator.IsEmpty]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.IsEmpty}`),
      [RuleOperator.IsNotEmpty]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.IsNotEmpty}`),
      [RuleOperator.In]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.In}`),
      [RuleOperator.NotIn]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotIn}`),
      [RuleOperator.DateIsWithinNext]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsWithinNext}`),
      [RuleOperator.DateIsNotWithinNext]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsNotWithinNext}`),
      [RuleOperator.InASingleGeofence]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.InASingleGeofence}`),
      [RuleOperator.NotInASingleGeofence]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotInASingleGeofence}`),
      [RuleOperator.EqualNoCase]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.EqualNoCase}`),
      [RuleOperator.NotEqualNoCase]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotEqualNoCase}`)
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onConfirmField(response: boolean) {
    this.showSelectionModal = response;
  }

  selectedFieldResponse(response: string) {
    this.filter.value = '{{ ' + response + ' }}';
  }

  setFilterTypeFlags() {
    this.isGenericFilter = false;
    this.isWorkflowFilter = false;
    this.isDateTimeFilter = false;
    this.isBooleanFilter = false;
    this.isDistanceFilter = false;
    this.isPicklistFilter = false;
    this.isInFilter = false;
    this.isGeofenceFilter = false;

    if (!!!this.filter) {
      return;
    }

    if (
      this.filterDataType === FieldDataType.Text &&
      (this.filter?.operator === RuleOperator.In || this.filter?.operator === RuleOperator.NotIn)
    ) {
      this.isInFilter = true;
      return;
    }

    if (
      this.filterDataType == FieldDataType.Geometry &&
      (this.filter?.operator == RuleOperator.InASingleGeofence || this.filter?.operator == RuleOperator.NotInASingleGeofence)
    ) {
      this.populateGeofenceDocuments();
      this.isGeofenceFilter = true;
      return;
    }

    if (
      this.filterDataType !== FieldDataType.UId &&
      this.filterDataType !== FieldDataType.DateTime &&
      this.filterDataType !== FieldDataType.Distance &&
      this.filterDataType !== FieldDataType.Picklist &&
      this.filterDataType !== FieldDataType.Boolean &&
      this.filter?.operator !== RuleOperator.IsEmpty &&
      this.filter?.operator !== RuleOperator.IsNotEmpty
    ) {
      this.isGenericFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.UId) {
      this.isWorkflowFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.DateTime) {
      this.isDateTimeFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.Boolean) {
      this.isBooleanFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.Distance) {
      this.initializeDistanceDisplayValue(this.filter);
      this.isDistanceFilter = true;
      return;
    }

    if (
      this.filterDataType === FieldDataType.Picklist &&
      (this.filter.operator === RuleOperator.Equal || this.filter.operator === RuleOperator.NotEqual)
    ) {
      this.isPicklistFilter = true;
    }
  }

  getPicklistValues(schema: DocumentSchema): void {
    this.picklistValues = {};

    if (!!schema) {
      schema.fields?.map(f => {
        if (f.dataType === FieldDataType.Picklist) {
          this.picklistService
            .getPicklistValues(f)
            .pipe(take(1))
            .subscribe(picklistValues => {
              this.picklistValues[f.systemName] = picklistValues;
            });
        }
      });
    }
  }

  getSchemaFields(fields: SchemaField[]): SchemaField[] {
    if (!fields) {
      return [];
    }

    return fields
      .filter(f => (f.fieldType & SystemFieldType.NotSelectable) !== SystemFieldType.NotSelectable)
      .filter(f => {
        return (
          (f.fieldType & SystemFieldType.Hidden) !== SystemFieldType.Hidden ||
          f.systemName === 'external_id' ||
          f.systemName === 'workflow_step_uid' ||
          f.systemName === 'date_created' ||
          f.systemName === 'date_updated'
        );
      })
      .sort((f1, f2) => (f1.systemName > f2.systemName ? 1 : -1));
  }

  onFocus(elm: any, event: any): void {
    if (elm.classList.contains('is-active')) {
      elm.classList.remove('is-active');
      this.showSelection = false;

      this.unsubscribeToClicks();
    } else {
      elm.classList.add('is-active');
      this.showSelection = true;

      this.subscribeToClicks(elm);
    }
  }

  subscribeToClicks(elm: any) {
    if (this.clickSubscription) {
      return;
    }

    this.clickSubscription = this.eventListenerService.clickEvents$.pipe(takeUntil(this.destroy$)).subscribe(target => {
      if (!elm.contains(target)) {
        elm.classList.remove('is-active');
        this.showSelection = true;

        this.unsubscribeToClicks();
      }
    });
  }

  unsubscribeToClicks() {
    if (this.clickSubscription) {
      this.showSelection = false;
      this.clickSubscription.unsubscribe();
      this.clickSubscription = null;
    }
  }

  selectDsField() {
    this.showSelection = false;
    this.showSelectionModal = true;
  }

  getOperators(filter: LinkFilter): { name: string; value: RuleOperator }[] {
    let operators: { name: string; value: RuleOperator }[] = [];

    if (!this.dataset?.schema) {
      operators = this.getDefaultOperators();
      return operators;
    }

    const field = this.dataset?.schema.fields.find(f => f.systemName === filter?.fromSystemField);
    if (!field) {
      operators = this.getDefaultOperators();
      return operators;
    }

    switch (field.dataType) {
      case FieldDataType.Color:
        operators = this.getColorOperators();
        break;

      case FieldDataType.DateTime:
        operators = this.getDateTimeOperators();
        break;

      case FieldDataType.Distance:
        operators = this.getDistanceOperators();
        break;

      case FieldDataType.Email:
        operators = this.getEmailOperators();
        break;

      case FieldDataType.Geometry:
        operators = this.getGeometryOperators();
        break;

      case FieldDataType.Boolean:
        operators = this.getBooleanOperators();
        break;

      case FieldDataType.Image:
        operators = this.getImageOperators();
        break;

      case FieldDataType.MultiPicklist:
        operators = this.getMultiPicklistOperators();
        break;

      case FieldDataType.Number:
        operators = this.getNumberOperators();
        break;

      case FieldDataType.Phone:
        operators = this.getPhoneOperators();
        break;

      case FieldDataType.Picklist:
        operators = this.getPicklistOperators();
        break;

      case FieldDataType.Text:
        operators = this.getTextOperators();
        break;

      case FieldDataType.User:
        operators = this.getUserOperators();
        break;

      case FieldDataType.UId:
        operators = this.getUIdOperators();
        break;

      case FieldDataType.Url:
        operators = this.getUrlOperators();
        break;

      case FieldDataType.Array:
        operators = this.getArrayOperators();
        break;
    }

    const existingFilter = operators.find(o => o.value === this.filter?.operator);
    if (!existingFilter) {
      this.filter.operator = operators[0].value;
    }

    return operators;
  }

  getDefaultOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo }
    ];

    return operators;
  }

  getColorOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual }
    ];

    return operators;
  }

  getDateTimeOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo }
    ];

    return operators;
  }

  getDistanceOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo }
    ];

    return operators;
  }

  getGeometryOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual }
    ];

    return operators;
  }

  getBooleanOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal }
    ];

    return operators;
  }

  getImageOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual }
    ];

    return operators;
  }

  getMultiPicklistOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual }
    ];

    return operators;
  }

  getNumberOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo }
    ];

    return operators;
  }

  getPicklistOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual }
    ];

    return operators;
  }

  getTextOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getEmailOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getPhoneOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getUIdOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual }
    ];

    return operators;
  }

  getUrlOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getUserOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getArrayOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getSelectedDataType(filter: LinkFilter): FieldDataType {
    if (!filter || !this.dataset?.schema) {
      return FieldDataType.Text;
    }

    const field = this.dataset?.schema.fields.find(f => f.systemName === filter.fromSystemField);
    return field?.dataType || FieldDataType.Text;
  }

  getWorkflowStepStatusName(filter: LinkFilter): string {
    const step = this.dataset?.workflow?.workflowSteps?.find(ws => ws.uId === filter.value);
    return step?.statusName;
  }

  getWorkflowStepStatusColorCode(filter: LinkFilter): string {
    const step = this.dataset?.workflow?.workflowSteps?.find(ws => ws.uId === filter.value);
    return step?.statusHtmlColorCode;
  }

  fieldTrack(index, item: SchemaField) {
    return item.uId;
  }

  fieldChange() {
    this.filter.value = null;
    this.filter.options = null;
    this.filterDataType = this.getSelectedDataType(this.filter);
    this.operators = this.getOperators(this.filter);
    this.setFilterTypeFlags();
    this.getToFields();
    this.filterChanged.emit(this.filter);
  }

  getToFields(): void {
    const field = this.dataset.schema.fields.find(f => f.systemName === this.filter?.fromSystemField);
    this.toSchemaFields = this.targetDataset?.schema.fields.filter(f => f.dataType === field?.dataType);
  }

  operatorTrack(index, item: RuleOperator) {
    return item;
  }

  operatorChange() {
    let wasGenericFilter = this.isGenericFilter;
    this.setFilterTypeFlags();
    // When text value not used by operator
    // 1. Save away filter value
    // 2. Clear filter value
    if (wasGenericFilter && !this.isGenericFilter) {
      this.lastFilterValue = this.filter.value;
      this.filter.value = null;
    }
    // When text value once again used by operator
    // 1. Restore filter value
    if (!wasGenericFilter && this.isGenericFilter && !this.filter.value && this.lastFilterValue) {
      this.filter.value = this.lastFilterValue;
    }
  }

  updateDistanceValue(val: any, filter: LinkFilter) {
    filter.value = (val * this.unitsConversionValue).toString();
  }

  workflowStepSelected(filter: LinkFilter, workflowStep: WorkflowStep) {
    filter.value = workflowStep.uId;
  }

  initializeDistanceDisplayValue(filter: LinkFilter): boolean {
    if (filter.value && !filter.displayValue) {
      filter.displayValue = filter.value / this.unitsConversionValue;
    }

    return true;
  }

  deleteFilterClick(): void {
    const index = this.filterGroup.filters.indexOf(this.filter);
    this.filterGroup.filters.splice(index, 1);
  }

  addInFilterClick(): void {
    if (!this.inFilterText) {
      return;
    }

    this.filter.value = this.filter?.value?.length > 0 ? `${this.filter.value}||${this.inFilterText}` : this.inFilterText;
    this.inFilterText = '';
  }

  removeInFilterClick(itemToRemove: string): void {
    const values = this.filter?.value?.split('||') ?? [];
    const idx = values.indexOf(itemToRemove);
    if (idx < 0) {
      return;
    }

    values.splice(idx, 1);

    this.filter.value = values.length > 0 ? values.join('||') : '';
  }

  getInFilterItems(): any[] {
    return this.filter.value?.split('||')?.filter(v => v?.length > 0);
  }

  geofenceDatasetOnChange(datasetUId): void {
    const dataset = this.geofenceDatasets.find(ds => ds.uId === datasetUId);
    this.setGeofenceFilterValue(
      dataset.organizationUId,
      dataset.uId,
      dataset.sourceOrgUId,
      dataset.sourceDatasetUId,
      dataset.systemDataTableName,
      this.geofenceDocumentUId
    );

    this.datasetChanged = true;
    this.populateGeofenceDocuments();
    this.changeDetector.markForCheck();
  }

  populateGeofenceDocuments(): void {
    const selectedValues = this.filter.value?.split('||')?.filter(v => v?.length > 0);
    if (!selectedValues || (selectedValues.length === 0 && !this.geofenceDatasets)) {
      return;
    }

    this.geofenceService
      .getGeofenceDocuments(selectedValues[1])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(docs => {
        this.geofenceDocuments = docs;

        if (!!docs && docs.length > 0 && !!this.geofenceDatasets) {
          if (!this.geofenceDocumentUId || this.datasetChanged) {
            this.geofenceDocumentUId = docs[0].uid;
            this.datasetChanged = false;
          }

          const dataset = this.geofenceDatasets.find(ds => ds.uId === this.geofenceDatasetUId);
          this.setGeofenceFilterValue(
            dataset.organizationUId,
            dataset.uId,
            dataset.sourceOrgUId,
            dataset.sourceDatasetUId,
            dataset.systemDataTableName,
            this.geofenceDocumentUId
          );
        }
        this.changeDetector.markForCheck();
      });
  }

  geofenceDocumentOnChange(documentUId): void {
    const dataset = this.geofenceDatasets.find(ds => ds.uId === this.geofenceDatasetUId);
    this.setGeofenceFilterValue(
      dataset.organizationUId,
      dataset.uId,
      dataset.sourceOrgUId,
      dataset.sourceDatasetUId,
      dataset.systemDataTableName,
      documentUId
    );
    this.changeDetector.markForCheck();
  }

  getGeofenceValues(filter: LinkFilter): void {
    if (filter.operator === RuleOperator.InASingleGeofence || filter.operator === RuleOperator.NotInASingleGeofence) {
      const selectedValues = this.filter.value?.split('||')?.filter(v => v?.length > 0);
      if (!!selectedValues && selectedValues.length > 1) {
        this.geofenceDatasetUId = selectedValues[1];
      }

      if (!!selectedValues && selectedValues.length > 5) {
        this.geofenceDocumentUId = selectedValues[5];
      }
    }
    this.changeDetector.markForCheck();
  }

  setGeofenceFilterValue(
    organizationUId: string,
    datasetUId: string,
    sourceOrganizationUId: string,
    sourceDatasetUId: string,
    systemDataTableName: string,
    documentUId: string
  ) {
    this.filter.value = `${organizationUId}||${datasetUId}||${sourceOrganizationUId}||${sourceDatasetUId}||${systemDataTableName}||${documentUId}`;
  }
}
